import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Table, Tag, Space, Select, Input, Typography, DatePicker, Row, Col, Button, Tooltip } from 'antd';
import {
  HistoryOutlined, LoginOutlined, PlusCircleOutlined, EditOutlined, DeleteOutlined,
  UploadOutlined, DownloadOutlined, CopyOutlined, EyeOutlined, ExportOutlined,
  ReloadOutlined, SearchOutlined, UpOutlined, DownOutlined,
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import dayjs, { Dayjs } from 'dayjs';
import { activityLogAPI } from '../services/api';
import { brand, gray, state } from '../theme/tokens';
import { EmptyState } from '../components/common';
import { useFitTableHeight } from '../hooks/useViewportFit';
import { fmtDateTimeSec } from '../utils/date';

const { Search } = Input;
const { RangePicker } = DatePicker;

const ACTION_OPTIONS = [
  { label: '전체', value: '' },
  { label: '로그인', value: 'LOGIN' },
  { label: '생성', value: 'CREATE' },
  { label: '수정', value: 'UPDATE' },
  { label: '삭제', value: 'DELETE' },
  { label: '업로드', value: 'UPLOAD' },
  { label: '다운로드', value: 'DOWNLOAD' },
  { label: '일괄등록', value: 'BULK_CREATE' },
  { label: '내보내기', value: 'EXPORT' },
  { label: '조회', value: 'VIEW' },
];

const TARGET_TYPE_OPTIONS = [
  { label: '전체', value: '' },
  { label: '인증', value: 'auth' },
  { label: '시험계획서', value: 'protocol' },
  { label: '시험일정', value: 'schedule' },
  { label: '마스터', value: 'master' },
  { label: '페이지', value: 'page' },
  { label: '파트너 페이지', value: 'partner_page' },
  { label: '파트너 계정', value: 'partner_account' },
];

const ROLE_OPTIONS = [
  { label: '전체 권한', value: '' },
  { label: '시스템관리자', value: 'ADMIN' },
  { label: '연구원', value: 'RESEARCHER' },
  { label: '선임연구원', value: 'Senior researcher' },
  { label: '열람자', value: 'VIEWER' },
];

const actionTagMap: Record<string, { icon: React.ReactNode; label: string }> = {
  LOGIN:       { icon: <LoginOutlined />,       label: '로그인' },
  CREATE:      { icon: <PlusCircleOutlined />,  label: '생성' },
  UPDATE:      { icon: <EditOutlined />,        label: '수정' },
  DELETE:      { icon: <DeleteOutlined />,      label: '삭제' },
  UPLOAD:      { icon: <UploadOutlined />,      label: '업로드' },
  DOWNLOAD:    { icon: <DownloadOutlined />,    label: '다운로드' },
  BULK_CREATE: { icon: <CopyOutlined />,        label: '일괄등록' },
  EXPORT:      { icon: <ExportOutlined />,      label: '내보내기' },
  VIEW:        { icon: <EyeOutlined />,         label: '조회' },
};

const ACTION_COLORS: Record<string, string> = {
  LOGIN: state.info, CREATE: state.success, UPDATE: state.warning,
  DELETE: state.error, UPLOAD: '#8b5cf6', DOWNLOAD: '#06b6d4',
  BULK_CREATE: brand.primaryHover, EXPORT: '#84cc16', VIEW: gray[400],
};

const targetTypeMap: Record<string, string> = {
  auth: '인증', protocol: '시험계획서', schedule: '시험일정',
  master: '마스터', page: '페이지', partner_page: '파트너 페이지', partner_account: '파트너 계정',
};

const ActivityLogPage: React.FC = () => {
  /* 표 본문 높이 실측 — 다른 목록 화면과 같은 공용 훅 (매직넘버 오버헤드 상수 제거) */
  const { tableWrapRef, cardWrapRef, bodyHeight } = useFitTableHeight();
  const [data, setData]       = useState<any[]>([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(false);
  /* 차트는 기본 접힘 — 목록을 보러 온 화면에서 30일 분석 차트가 310px(1366 기준 뷰포트의 40%)를
     써 표가 4행밖에 안 보였다 (2026-09-03 밀도 실측). 펼침/접힘 선택은 계정 브라우저에 기억시킨다 */
  const [chartCollapsed, setChartCollapsed] = useState(() => {
    try { return localStorage.getItem('sappt_activity_chart_open') !== '1'; } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem('sappt_activity_chart_open', chartCollapsed ? '0' : '1'); } catch { /* noop */ }
  }, [chartCollapsed]);
  const [showView, setShowView] = useState(false);
  const [chartData, setChartData] = useState<{ dates: string[]; series: Record<string, number[]>; total: number[] } | null>(null);
  const [filters, setFilters] = useState({
    action: '', target_type: '', role: '', keyword: '',
    date_from: '', date_to: '',
    page: 1, page_size: 20,
  });
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  const buildParams = useCallback(() => {
    const p: any = { ...filters };
    Object.keys(p).forEach(k => { if (p[k] === '' || p[k] == null) delete p[k]; });
    return p;
  }, [filters]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, statsRes] = await Promise.all([
        activityLogAPI.list(buildParams()),
        activityLogAPI.stats(buildParams()),
      ]);
      setData(listRes.data.items);
      setTotal(listRes.data.total);
      setChartData(statsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDateRange = (range: [Dayjs | null, Dayjs | null] | null) => {
    setDateRange(range);
    setFilters(prev => ({
      ...prev,
      date_from: range?.[0]?.format('YYYY-MM-DD') || '',
      date_to:   range?.[1]?.format('YYYY-MM-DD') || '',
      page: 1,
    }));
  };

  // 날짜별 스택 바 차트 (VIEW 토글)
  const barOption = useMemo(() => {
    if (!chartData || chartData.dates.length === 0) return null;
    const { dates, series } = chartData;
    const BAR_ORDER = ['LOGIN', 'CREATE', 'UPDATE', 'DELETE', 'UPLOAD', 'DOWNLOAD', 'EXPORT', 'BULK_CREATE', 'PARTNER_CONFIRM'];
    const visible = showView ? [...BAR_ORDER, 'VIEW'] : BAR_ORDER;
    const present = visible.filter(a => series[a]);
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any[]) =>
          `<b>${params[0].axisValue}</b><br/>` +
          params.filter(p => p.value > 0).map(p => `${p.marker}${p.seriesName}: <b>${p.value}</b>`).join('<br/>'),
      },
      legend: { top: 4, type: 'scroll', textStyle: { fontSize: 11 } },
      grid: { left: 38, right: 12, top: 46, bottom: 34 },
      xAxis: {
        type: 'category',
        data: dates,
        axisLabel: { fontSize: 10, rotate: dates.length > 14 ? 30 : 0, formatter: (v: string) => v.slice(5) },
      },
      yAxis: { type: 'value', axisLabel: { fontSize: 10 }, minInterval: 1 },
      series: present.map(act => ({
        name: actionTagMap[act]?.label || act,
        type: 'bar',
        stack: 'total',
        data: series[act] || [],
        itemStyle: { color: ACTION_COLORS[act] || '#94a3b8' },
      })),
    };
  }, [chartData, showView]);

  // 액션 분포 도넛 차트
  const donutOption = useMemo(() => {
    if (!chartData) return null;
    const { series } = chartData;
    const donutData = Object.entries(series)
      .map(([act, vals]) => ({
        name: actionTagMap[act]?.label || act,
        value: vals.reduce((a, b) => a + b, 0),
        itemStyle: { color: ACTION_COLORS[act] || '#94a3b8' },
      }))
      .filter(d => d.value > 0)
      .sort((a, b) => b.value - a.value);
    const totalSum = donutData.reduce((a, b) => a + b.value, 0);
    return {
      tooltip: {
        trigger: 'item',
        formatter: (p: any) => `${p.marker}${p.name}: <b>${p.value.toLocaleString()}</b> (${p.percent}%)`,
      },
      legend: {
        orient: 'vertical', right: 4, top: 'center',
        textStyle: { fontSize: 11 },
        formatter: (name: string) => {
          const item = donutData.find(d => d.name === name);
          return item ? `${name}  ${item.value.toLocaleString()}` : name;
        },
      },
      graphic: [
        { type: 'text', left: '31%', top: '43%', style: { text: totalSum.toLocaleString(), textAlign: 'center', fill: gray[800], fontSize: 15, fontWeight: 'bold' } },
        { type: 'text', left: '31%', top: '54%', style: { text: '건', textAlign: 'center', fill: gray[500], fontSize: 11 } },
      ],
      series: [{
        type: 'pie',
        radius: ['42%', '66%'],
        center: ['32%', '50%'],
        label: { show: false },
        labelLine: { show: false },
        data: donutData,
      }],
    };
  }, [chartData]);

  const columns = [
    {
      title: '일시', dataIndex: 'created_at', key: 'created_at', width: 170,
      render: (v: string) => fmtDateTimeSec(v),
    },
    {
      title: '사용자', dataIndex: 'username', key: 'username', width: 100,
      render: (v: string) => <Typography.Text strong style={{ color: brand.primary }}>{v}</Typography.Text>,
    },
    {
      title: '액션', dataIndex: 'action', key: 'action', width: 100,
      render: (v: string) => {
        const info = actionTagMap[v] || { icon: null, label: v };
        const bg = ACTION_COLORS[v] || '#94a3b8';
        return (
          <Tag
            icon={info.icon}
            style={{
              background: bg,
              borderColor: bg,
              color: '#fff',
              fontWeight: 600,
              fontSize: 11,
              padding: '1px 8px',
            }}
          >
            {info.label}
          </Tag>
        );
      },
    },
    {
      title: '대상', dataIndex: 'target_type', key: 'target_type', width: 100,
      render: (v: string) => <Tag>{targetTypeMap[v] || v}</Tag>,
    },
    { title: '대상명', dataIndex: 'target_name', key: 'target_name', ellipsis: true },
    {
      title: '상세', dataIndex: 'detail', key: 'detail', ellipsis: true,
      render: (v: string) => v || '-',
    },
    {
      title: 'IP', dataIndex: 'ip_address', key: 'ip_address', width: 130,
      render: (v: string) => <Typography.Text type="secondary" style={{ fontSize: 12 }}>{v || '-'}</Typography.Text>,
    },
    {
      title: '위치', key: 'ip_location', width: 200,
      render: (_: any, r: any) => {
        const parts = [r.ip_country, r.ip_city].filter(Boolean);
        return <Typography.Text type="secondary" style={{ fontSize: 12 }}>{parts.join(' ') || '-'}</Typography.Text>;
      },
    },
  ];

  return (
    <div>

      {/* 차트 카드 */}
      <Card size="small" style={{ flexShrink: 0, marginBottom: 12 }}
        bodyStyle={{ padding: chartCollapsed ? 0 : '8px 16px', overflow: 'hidden' }}
        title={
          <Space>
            <HistoryOutlined style={{ color: brand.primary }} />
            <span style={{ fontWeight: 600, fontSize: 13 }}>활동 분석</span>
            {!chartCollapsed && chartData && chartData.dates.length > 0 && (
              <Typography.Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                ({chartData.dates[0]} ~ {chartData.dates[chartData.dates.length - 1]})
              </Typography.Text>
            )}
          </Space>
        }
        extra={
          <Space size={4}>
            {!chartCollapsed && (
              <Tooltip title="VIEW(페이지 조회) 건수는 압도적으로 많아 기본 제외됩니다">
                <Button
                  size="small"
                  type={showView ? 'primary' : 'default'}
                  onClick={() => setShowView(v => !v)}
                  style={{ fontSize: 11, height: 24, padding: '0 8px' }}
                >
                  VIEW {showView ? '포함' : '제외'}
                </Button>
              </Tooltip>
            )}
            <Tooltip title={chartCollapsed ? '차트 펼치기' : '차트 접기'}>
              <Button
                type="text" size="small"
                icon={chartCollapsed ? <DownOutlined /> : <UpOutlined />}
                onClick={() => setChartCollapsed(prev => !prev)}
              />
            </Tooltip>
          </Space>
        }
      >
        {!chartCollapsed && (
          barOption
            ? (
              <Row gutter={16}>
                <Col span={15}>
                  <ReactECharts option={barOption} style={{ height: 240 }} />
                </Col>
                <Col span={9}>
                  {donutOption
                    ? <ReactECharts option={donutOption} style={{ height: 240 }} />
                    : null}
                </Col>
              </Row>
            )
            : <div style={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', color: gray[400] }}>데이터가 없습니다</div>
        )}
      </Card>

      {/* 목록 카드 — 표 본문 높이를 실측해 페이지네이션이 뷰포트 바닥에 머문다 */}
      <div ref={cardWrapRef}>
      <Card
        bodyStyle={{ padding: '12px 16px 0' }}
        title={
          <Space>
            <HistoryOutlined style={{ color: brand.primary }} />
            <span>활동이력</span>
            <Typography.Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>
              총 {total}건
            </Typography.Text>
          </Space>
        }
        extra={<Button size="small" icon={<ReloadOutlined />} onClick={fetchData} />}
      >
        {/* 필터 바 */}
        <Row gutter={[8, 8]} style={{ marginBottom: 10, flexShrink: 0 }} align="middle">
          <Col>
            <RangePicker
              size="small"
              value={dateRange}
              onChange={handleDateRange}
              placeholder={['시작일', '종료일']}
              style={{ width: 220 }}
              allowClear
              presets={[
                { label: '오늘',        value: [dayjs(), dayjs()] },
                { label: '최근 7일',    value: [dayjs().subtract(6, 'day'), dayjs()] },
                { label: '이번 달',     value: [dayjs().startOf('month'), dayjs()] },
                { label: '지난 달',     value: [dayjs().subtract(1,'month').startOf('month'), dayjs().subtract(1,'month').endOf('month')] },
                { label: '최근 3개월',  value: [dayjs().subtract(2,'month').startOf('month'), dayjs()] },
              ]}
            />
          </Col>
          <Col>
            <Select
              value={filters.action}
              onChange={v => setFilters(prev => ({ ...prev, action: v, page: 1 }))}
              options={ACTION_OPTIONS}
              size="small" style={{ width: 110 }}
              placeholder="액션"
            />
          </Col>
          <Col>
            <Select
              value={filters.target_type}
              onChange={v => setFilters(prev => ({ ...prev, target_type: v, page: 1 }))}
              options={TARGET_TYPE_OPTIONS}
              size="small" style={{ width: 120 }}
              placeholder="대상유형"
            />
          </Col>
          <Col>
            <Select
              value={filters.role}
              onChange={v => setFilters(prev => ({ ...prev, role: v, page: 1 }))}
              options={ROLE_OPTIONS}
              size="small" style={{ width: 120 }}
              placeholder="권한"
            />
          </Col>
          <Col>
            <Search
              placeholder="대상명/상세 검색"
              allowClear size="small"
              onSearch={v => setFilters(prev => ({ ...prev, keyword: v, page: 1 }))}
              style={{ width: 220 }}
            />
          </Col>
        </Row>

        <style>{Object.entries(ACTION_COLORS).map(([k, c]) =>
          `.row-action-${k} td:first-child { border-left: 3px solid ${c} !important; }`
        ).join('\n')}</style>
        <div ref={tableWrapRef}>
        <Table
          dataSource={data}
          columns={columns}
          rowKey="id"
          loading={loading}
          size="small"
          scroll={{ x: 'max-content', y: bodyHeight }}
          locale={{ emptyText: <EmptyState title="활동이력이 없습니다" size="sm" /> }}
          rowClassName={(r: any) => `row-action-${r.action}`}
          pagination={{
            current: filters.page,
            pageSize: filters.page_size,
            total,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100', '200'],
            showTotal: (t, r) => `${r[0]}–${r[1]} / 총 ${t}건`,
            size: 'small',
            onChange: (page, pageSize) => setFilters(prev => ({ ...prev, page, page_size: pageSize })),
          }}
        />
        </div>
      </Card>
      </div>
    </div>
  );
};

export default ActivityLogPage;
